import { useMemo, useRef, useEffect } from 'react';
import { processAnalysisText } from '@/lib/text-pipeline';
import { useCardStatusEnrich } from '@/hooks/useCardStatusEnrich';
import { showCardTooltip, hideCardTooltip } from '@/components/CardTooltip';
import { cardImageUris } from '@/lib/utils';

interface AnalysisTextProps {
  text: string;
}

/**
 * AnalysisText — renders analysis text through the text processing pipeline.
 *
 * Uses processAnalysisText() for markdown, [[Card Name]] tooltips, mana symbols,
 * and status badges. Card tooltip hover is attached via useEffect delegation.
 */
export function AnalysisText({ text }: AnalysisTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const { html } = useMemo(() => {
    if (!text) return { html: '<p>No analysis available</p>', toc: [] };
    return processAnalysisText(text);
  }, [text]);

  // Enrich card spans with GC/banned badges after render
  useCardStatusEnrich(containerRef, [html]);

  // Attach card tooltip hover events to .decklist-card spans via event delegation
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseOver = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.('.decklist-card[data-card-name]');
      if (target) {
        const name = target.getAttribute('data-card-name');
        if (name) showCardTooltip(cardImageUris(name), e);
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.('.decklist-card[data-card-name]');
      if (target) hideCardTooltip();
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
    };
  }, [html]);

  return (
    <div
      ref={containerRef}
      className="prose max-w-none text-on-surface"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
